import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useEffect, useRef, useCallback } from 'react';

// Store the active listener's callbacks globally
let activeListener: {
  id: string;
  setIsKeyDown: React.Dispatch<React.SetStateAction<boolean>>;
  setQmate: React.Dispatch<React.SetStateAction<string>>;
  inputbox: HTMLInputElement | null;
  sendButton: HTMLButtonElement | HTMLInputElement | null;
} | null = null;

function VoiceSearch(
  setIsKeyDown: React.Dispatch<React.SetStateAction<boolean>>,
  inputbox: HTMLInputElement | null,
  sendButton: HTMLButtonElement | HTMLInputElement | null,
  setQmate: React.Dispatch<React.SetStateAction<string>>,
  uniqueId: string
) {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleStartListening = useCallback(() => {
    // Store this instance as active with its callbacks
    activeListener = {
      id: uniqueId,
      setIsKeyDown,
      setQmate,
      inputbox,
      sendButton
    };
    
    resetTranscript();
    SpeechRecognition.startListening();
    if (sendButton) {
      sendButton.disabled = true;
    }
  }, [uniqueId, setIsKeyDown, setQmate, inputbox, sendButton, resetTranscript]);

  const handleStopListening = useCallback(() => {
    // Only process if this instance is the active one
    if (!activeListener || activeListener.id !== uniqueId) {
      return;
    }
    
    SpeechRecognition.stopListening();
    
    // Use the stored callbacks from when listening started
    activeListener.setIsKeyDown(false);
    activeListener.setQmate(transcript);
    
    if (activeListener.inputbox) {
      activeListener.inputbox.removeAttribute('disabled');
    }
    if (activeListener.sendButton) {
      activeListener.sendButton.removeAttribute('disabled');
    }
    
    // Clear active listener
    activeListener = null;
  }, [uniqueId, transcript]);

  useEffect(() => {
    // Only handle listening state if this instance is active
    if (!activeListener || activeListener.id !== uniqueId) {
      return;
    }

    if (listening) {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    } else {
      silenceTimeoutRef.current = setTimeout(() => {
        handleStopListening();
      }, 500);
    }

    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, [listening, uniqueId, handleStopListening]);

  return {
    handleStartListening,
    handleStopListening,
  };
}

export default VoiceSearch;